import React, { useEffect, useRef, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import SourceIcon from './SourceIcon';
import { groupAdjacentLinks } from '../lib/linkGrouping';

const SourceIconInjector = ({ children, store, onVisibilityChange }) => {
  const containerRef = useRef(null);
  // Track injected groups by their sorted source-ID key → React root
  const injectedGroupsRef = useRef(new Map());
  const iconRootsRef = useRef([]);
  const debounceTimerRef = useRef(null);

  const injectIcons = useCallback(() => {
    if (!containerRef.current || !store) return;

    // Find all links with data-source-id
    const links = Array.from(
      containerRef.current.querySelectorAll('a[data-source-id]')
    );

    if (links.length === 0) return;

    // Group adjacent links
    const groups = groupAdjacentLinks(links);

    groups.forEach(group => {
      const lastLink = group[group.length - 1];

      // Collect all source IDs from the group
      const sourceIds = group.flatMap(link => {
        const ids = link.getAttribute('data-source-id');
        return ids ? ids.split(',').map(id => id.trim()) : [];
      }).filter(id => id);

      if (sourceIds.length === 0) return;

      // Stable key for this group — sorted so order doesn't matter
      const groupKey = [...sourceIds].sort().join(',');

      if (injectedGroupsRef.current.has(groupKey)) {
        // Group already has an icon container — check it's still in the DOM and
        // still adjacent to the correct link; if not, re-inject.
        const existingContainer = injectedGroupsRef.current.get(groupKey).container;
        if (existingContainer && existingContainer.parentNode &&
            lastLink.nextElementSibling === existingContainer) {
          return; // Already correctly injected
        }
        // Container moved or was removed — unmount and re-inject below
        try { injectedGroupsRef.current.get(groupKey).root.unmount(); } catch (_) {}
        existingContainer?.remove();
        injectedGroupsRef.current.delete(groupKey);
      }

      // Also check the next sibling for an existing container (e.g. from a previous mount)
      const existingIcon = lastLink.nextElementSibling;
      if (existingIcon && existingIcon.classList.contains('source-icon-container')) {
        // Adopt the existing container without re-injecting
        return;
      }

      const iconContainer = document.createElement('span');
      iconContainer.className = 'source-icon-container';
      lastLink.after(iconContainer);

      const root = createRoot(iconContainer);
      root.render(<SourceIcon sourceIds={sourceIds} store={store} />);

      injectedGroupsRef.current.set(groupKey, { root, container: iconContainer });
      iconRootsRef.current.push(root);
    });
  }, [store]);

  useEffect(() => {
    // Use minimal debounce to inject icons quickly during streaming
    // This prevents the "jumping" effect where icons appear after text
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      injectIcons();
    }, 16); // ~1 frame at 60fps for smooth appearance

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [children, injectIcons]);

  // Listen to visibility change callback from parent (triggered when conversation expands)
  useEffect(() => {
    if (onVisibilityChange) {
      // Trigger injection when parent signals visibility change
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        injectIcons();
      }, 16);
    }
  }, [onVisibilityChange, injectIcons]);

  // Cleanup on unmount — defer to avoid "synchronous unmount during render" warning
  useEffect(() => {
    return () => {
      const roots = iconRootsRef.current.slice();
      setTimeout(() => {
        roots.forEach(root => {
          try {
            root.unmount();
          } catch (e) {
            // Ignore
          }
        });
      }, 0);
    };
  }, []);

  return (
    <div ref={containerRef}>
      {children}
    </div>
  );
};

export default SourceIconInjector;